import React, { FC } from "react";
import {
  Checkbox,
  FormControlLabel,
  Grid2 as Grid,
  MenuItem,
  Radio,
  Stack,
  TextField,
} from "@mui/material";
import CustomLabelInput from "@components/CustomLabelInput";
import { useTranslation } from "react-i18next";
import { Controller, UseFormReturn } from "react-hook-form";
import { ActivityType } from "@models/activity-type";
import ReactHookFormSelect from "@components/ReactHookFormSelect/ReactHookFormSelect";
import ReactHookFormDatePicker from "@components/ReactHookFormDatePicker/ReactHookFormDatePicker";
import ReactHookFormTimePicker from "@components/ReactHookFormTimePicker";
import { ReminderFrequency } from "@models/reminder-frequency";
import { DayOfWeek } from "@models/day-of-week";
import ReactHookFormCheckbox from "@components/ReactHookFormCheckbox";
import ReactHookFormRadioButtonGroup from "@components/ReactHookFormRadioButtonGroup/ReactHookFormRadioButtonGroup";

export interface ReminderFormType {
  title?: string;
  description?: string;
  activityType?: ActivityType;
  startDate?: Date | null;
  time?: Date | null;
  frequency?: ReminderFrequency;
  dayOfWeek: Record<DayOfWeek, boolean>;
  isActive: boolean;
}

const ReminderForm: FC<{ form: UseFormReturn<ReminderFormType> }> = ({
  form: {
    watch,
    register,
    control,
    formState: { errors },
  },
}) => {
  const { t } = useTranslation();

  const frequency = watch("frequency");

  return (
    <Stack gap="16px">
      <CustomLabelInput
        label={t("schedule.drawer.form.nameInput.label")}
        id="reminderTitle"
        required={true}
      >
        <TextField
          {...register(`title`, { required: true })}
          error={!!errors?.title}
          id="reminderTitle"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("schedule.drawer.form.nameInput.placeholder")}
        />
      </CustomLabelInput>

      <CustomLabelInput
        label={t("schedule.drawer.form.descriptionInput.label")}
        id="reminderDescription"
        required={true}
      >
        <TextField
          {...register(`description`, { required: true })}
          error={!!errors?.description}
          multiline
          rows={3}
          id="reminderDescription"
          type="text"
          size="small"
          variant="outlined"
          fullWidth
          placeholder={t("schedule.drawer.form.descriptionInput.placeholder")}
        />
      </CustomLabelInput>

      <Stack maxWidth="50%" minWidth="100px" gap="16px">
        <ReactHookFormSelect
          size="small"
          label={t("schedule.drawer.form.typeInput.label")}
          placeholder={t("schedule.drawer.form.typeInput.placeholder")}
          displayValueFunction={(v: string) => t(`schedule.types.${v}`)}
          fullWidth={true}
          controller={{
            control,
            rules: { required: true },
            name: "activityType",
            defaultValue: ActivityType.Conversation,
          }}
          error={!!errors?.activityType}
          required={true}
        >
          {Object.values(ActivityType).map((type) => (
            <MenuItem key={type} value={type}>
              {t(`schedule.types.${type}`)}
            </MenuItem>
          ))}
        </ReactHookFormSelect>

        <CustomLabelInput
          label={t("schedule.drawer.form.startDateInput.label")}
          id="reminderStartDate"
        >
          <ReactHookFormDatePicker
            slotProps={{
              textField: {
                size: "small",
                error: !!errors?.startDate,
              },
            }}
            id="reminderStartDate"
            controller={{
              name: "startDate",
              control,
              rules: {required: frequency === ReminderFrequency.OneTime},
              defaultValue: null,
            }}
          />
        </CustomLabelInput>

        <CustomLabelInput
          label={t("schedule.drawer.form.timeInput.label")}
          id="reminderTime"
          required={true}
        >
          <ReactHookFormTimePicker
            slotProps={{
              textField: {
                size: "small",
                error: !!errors?.time,
              },
            }}
            id="reminderTime"
            controller={{
              name: "time",
              control,
              defaultValue: null,
              rules: { required: true },
            }}
          />
        </CustomLabelInput>
      </Stack>

      <ReactHookFormRadioButtonGroup
        label={t("schedule.drawer.form.frequencySelect.label")}
        controller={{
          control,
          rules: { required: true },
          name: "frequency",
          defaultValue: ReminderFrequency.OneTime,
        }}
        error={!!errors.frequency}
        required={true}
      >
        <Stack flexDirection="row" gap="8px">
          {Object.values(ReminderFrequency).map((value) => (
            <FormControlLabel
              key={value}
              value={value}
              control={<Radio size="small" />}
              label={
                <>{t(`schedule.drawer.form.frequencySelect.type.${value}`)}</>
              }
            />
          ))}
        </Stack>
      </ReactHookFormRadioButtonGroup>

      {frequency === ReminderFrequency.Weekly && (
        <Grid container spacing={1} rowSpacing={0}>
          {Object.values(DayOfWeek).map((dayOfWeek) => {
            return (
              <Grid key={dayOfWeek} size={{ xs: 6, sm: 4, md: 3 }}>
                <FormControlLabel
                  control={
                    <Controller
                      control={control}
                      render={({ field }) => (
                        <Checkbox {...field} checked={field.value} />
                      )}
                      name={`dayOfWeek.${dayOfWeek}`}
                      defaultValue={false as any}
                    />
                  }
                  label={t(`dayOfWeek.${dayOfWeek}`)}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <ReactHookFormCheckbox
        id="reminderIsActive"
        controller={{
          name: "isActive",
          control,
          defaultValue: false,
        }}
        label={t("schedule.drawer.form.enabledInput.label")}
      />
    </Stack>
  );
};

export default ReminderForm;
